'use client';

import { Drawer } from '@/shared-ui/molecules/drawer';
import type { ShopperProducts } from '@/scapi';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMegaMenuContent } from '@/extensions/snk/lib/use-mega-menu-content';
import { parseMegaMenuCustomNavItems, type MegaMenuCustomNavItem } from '@/extensions/snk/lib/mega-menu-custom-nav';
import { useIsDesktopMegaMenu } from './lib/use-is-desktop-mega-menu';
import { DrawerContent, NavContent, type SubmenuPanelItem, type MegaMenuNavItem } from './ui';
import {
    BrandsMegaMenuPanel,
    CustomMegaMenuPanel,
    InsideSpaceMegaMenuPanel,
    NewInMegaMenuPanel,
    OffersMegaMenuPanel,
    TrendingMegaMenuPanel,
} from './ui/organisms/mega-menu-dropdown-panels';

const FIXED_MEGA_MENU_ITEMS: MegaMenuNavItem[] = [
    { id: 'brands', label: 'Brands', to: '/brands', link: false },
    { id: 'new-in', label: 'New In', to: '/new-in', link: false },
    { id: 'offers', label: 'Offers', to: '/offers', link: false },
    { id: 'inside-space', label: 'Inside Space', to: '/inside-space', link: false },
    { id: 'trending', label: 'Trending', to: '/trending', link: false },
];

type MegaMenuProps = {
    items?: MegaMenuNavItem[];
    submenuPanels?: SubmenuPanelItem[];
    rootCategory?: Promise<ShopperProducts.schemas['Category']>;
    shopAllLabel?: string;
    defaultDesktopItem?: string;
    /** Condensed header: the desktop nav bar drops its black band and inverts its colours. */
    condensed?: boolean;
};

export type { MegaMenuNavItem, SubmenuPanelItem };

function toCustomMegaMenuNavItem(item: MegaMenuCustomNavItem): MegaMenuNavItem {
    return {
        id: item.id,
        label: item.label,
        to: item.to,
        // Dropdown when columns exist; otherwise navigate to the merchant fallback URL.
        link: !item.hasDropdownContent,
        customSlot: item.slot,
    };
}

export const MegaMenu = ({
    items: itemsProp,
    submenuPanels: submenuPanelsProp,
    rootCategory,
    shopAllLabel = 'Shop all',
    defaultDesktopItem,
    condensed = false,
}: MegaMenuProps) => {
    const navContainerRef = useRef<HTMLDivElement>(null);
    const [activeDesktopItem, setActiveDesktopItem] = useState<string | undefined>(defaultDesktopItem);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const dropdownOpen = Boolean(activeDesktopItem);
    const isDesktopNav = useIsDesktopMegaMenu();
    const { brandsCategory, newInCategory, trendingContent, megaMenuEmbedded, isLoading } = useMegaMenuContent({
        enabled: isDesktopNav,
        open: dropdownOpen,
    });
    const panelIsLoading = dropdownOpen && isLoading;

    const fixedItems = itemsProp ?? FIXED_MEGA_MENU_ITEMS;
    const customNavItems = useMemo(() => parseMegaMenuCustomNavItems(megaMenuEmbedded), [megaMenuEmbedded]);
    const items = useMemo(
        () => [...fixedItems, ...customNavItems.map(toCustomMegaMenuNavItem)],
        [fixedItems, customNavItems]
    );

    const handleDesktopItemChange = (itemId: string) => {
        setActiveDesktopItem((prevActiveItem) => (prevActiveItem === itemId ? undefined : itemId));
    };

    const closeSubmenuPanel = useCallback(() => setActiveDesktopItem(undefined), []);

    const builtSubmenuPanels = useMemo<SubmenuPanelItem[]>(() => {
        const onNavigate = closeSubmenuPanel;
        const panelProps = {
            embeddedComponent: megaMenuEmbedded,
            isLoading: panelIsLoading,
            onNavigate,
        };

        const fixedPanels: SubmenuPanelItem[] = [
            {
                itemKey: 'brands',
                content: <BrandsMegaMenuPanel {...panelProps} brandsCategory={brandsCategory} />,
            },
            {
                itemKey: 'new-in',
                content: <NewInMegaMenuPanel {...panelProps} newInCategory={newInCategory} />,
            },
            {
                itemKey: 'offers',
                content: <OffersMegaMenuPanel {...panelProps} />,
            },
            {
                itemKey: 'inside-space',
                content: <InsideSpaceMegaMenuPanel {...panelProps} />,
            },
            {
                itemKey: 'trending',
                content: <TrendingMegaMenuPanel {...panelProps} trendingContent={trendingContent} />,
            },
        ];

        const customPanels: SubmenuPanelItem[] = customNavItems
            .filter((item) => item.hasDropdownContent)
            .map((item) => ({
                itemKey: item.id,
                content: <CustomMegaMenuPanel {...panelProps} slot={item.slot} />,
            }));

        return [...fixedPanels, ...customPanels];
    }, [
        megaMenuEmbedded,
        brandsCategory,
        newInCategory,
        trendingContent,
        panelIsLoading,
        closeSubmenuPanel,
        customNavItems,
    ]);

    const submenuPanels = submenuPanelsProp ?? builtSubmenuPanels;

    return (
        <Drawer.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
            <div ref={navContainerRef} className="w-full">
                <NavContent
                    items={items}
                    activeDesktopItem={activeDesktopItem}
                    setActiveDesktopItem={handleDesktopItemChange}
                    closeSubmenuPanel={closeSubmenuPanel}
                    submenuPanels={submenuPanels}
                    shopAllLabel={shopAllLabel}
                    ignorePanelCloseRefs={[navContainerRef]}
                    condensed={condensed}
                />
            </div>
            <DrawerContent rootCategory={rootCategory} open={drawerOpen} onOpenChange={setDrawerOpen} />
        </Drawer.Root>
    );
};
